import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import GallerySection from '../components/GallerySection';
import TestimonialsSection from '../components/TestimonialsSection';
import { imageUrl } from '../lib/images';

export default function CykelloebPage() {
  return (
    <>
      <SEO
        title="Cykelløb"
        description="Professionel foto- og videodækning af cykelløb og cykelsport. Fart, intensitet, feltets dynamik og rytternes kamp foreviget med skarp linse."
      />

      {/* Hero Section (Cinematic Dark Action) */}
      <section className="relative min-h-[75vh] flex items-center justify-center pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl('/_assets/115820782-172952827564552-2790911280586450986-n.jpg', 'width=1920,quality=85,format=auto')}
            alt="Cykelfelt i høj fart på dansk landevej"
            className="w-full h-full object-cover filter brightness-[0.45] contrast-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C0D] via-[#0C0C0D]/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 text-center space-y-6">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif font-medium text-[#F4EFEA] tracking-tight leading-tight max-w-4xl mx-auto">
            Fart, intensitet og kampen på asfalten
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-zinc-300 font-sans max-w-2xl mx-auto leading-relaxed font-normal">
            Cykelsport kræver mere end blot hurtige lukketider. Det kræver forståelse for feltets udvikling, rytterens fokus og stemningen i og omkring løbet.
          </p>

          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/kontakt?ydelse=cykelloeb"
              className="cta-btn inline-flex items-center justify-center px-8 py-3.5 bg-white hover:bg-stone-100 text-[#0C0C0D] font-semibold text-sm tracking-wide transition-all shadow-lg"
            >
              Book dækning af cykelløb
            </Link>
            <a
              href="#galleri"
              className="cta-btn inline-flex items-center justify-center px-8 py-3.5 bg-transparent hover:bg-transparent text-white font-medium text-sm tracking-wide transition-all border border-white"
            >
              Se cykelgalleriet
            </a>
          </div>
        </div>
      </section>

      {/* Light Editorial Story / Approach Section */}
      <section className="py-20 md:py-28 bg-white text-[#141416] border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-5 aspect-[4/5] rounded-none overflow-hidden border border-zinc-200 shadow-lg">
              <img
                src={imageUrl('/_assets/467843501-1084374129755746-8742697337945456892-n.jpg', 'width=800,quality=85,format=auto')}
                alt="Fokuseret cykelrytter i modstand"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="md:col-span-7 space-y-6">
              <h2 className="text-3xl sm:text-4xl font-serif text-[#141416] leading-tight">
                Fra den spændte stilhed før start til euforien over målstregen
              </h2>
              <div className="space-y-4 text-[#3A3836] text-sm md:text-base leading-relaxed font-sans">
                <p>
                  Et cykelløb rummer en helt særlig dynamik. Rytternes koncentration på startstregen, mekanikernes hurtige hænder, støvet og lyden af klikpedaler, inden feltet slippes løs.
                </p>
                <p>
                  Jeg følger løbet tæt – i svingene, på de afgørende stigninger og på opløbsstrækningen. Målet er at levere billeder og videoklip med nerve og kant, som arrangører, cykelhold, sponsorer og ryttere kan bruge med stolthed i markedsføring og pressearbejde.
                </p>
              </div>

              <div className="space-y-4 pt-4 border-t border-zinc-200 text-sm">
                <div>
                  <div className="text-[#141416] font-semibold mb-1">Arrangører & Løb</div>
                  <p className="text-[#575450] leading-relaxed">Komplet dækning af stemning, sponsorer og deltagere til presse og web.</p>
                </div>
                <div>
                  <div className="text-[#141416] font-semibold mb-1">Teams & Ryttere</div>
                  <p className="text-[#575450] leading-relaxed">Actionfotos, rytterprofiler og video til sociale medier og sponsorer.</p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/kontakt?ydelse=cykelloeb"
                  className="cta-btn inline-flex items-center gap-2 px-7 py-3.5 bg-[#141416] hover:bg-black text-white text-sm font-medium transition-colors"
                >
                  Book til cykelløb <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery from CMS (Dark Photographic Gallery) */}
      <div id="galleri">
        <GallerySection
          slug="cykelloeb"
          title="Billeder fra Asfalten"
          subtitle="Galleri · Cykelløb"
          className="bg-[#0C0C0D]"
        />
      </div>

      {/* Light Highlights & Deliverables Section */}
      <section className="py-20 md:py-28 bg-[#F3F4F6] text-[#141416] border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <h2 className="text-3xl sm:text-4xl font-serif text-[#141416]">
              Foto og video i én samlet pakke
            </h2>
            <p className="text-zinc-600 text-sm md:text-base">
              Når Michael er med på cykelløbet, får I alsidigt materiale, der fungerer på tværs af alle kanaler.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-8 rounded-none bg-white border border-zinc-200 shadow-sm space-y-4">
              <div className="text-[#141416]">
                <i className="fa-solid fa-camera text-2xl" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-serif text-[#141416]">Action & Feltbilleder</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Højhastighedsfotos af feltet, udbrud, spurter og tekniske passager i højeste trykkvalitet.
              </p>
            </div>

            <div className="p-8 rounded-none bg-white border border-zinc-200 shadow-sm space-y-4">
              <div className="text-[#141416]">
                <i className="fa-solid fa-film text-2xl" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-serif text-[#141416]">Videoklip & Reels</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Korte, dynamiske videoklip til Instagram, Facebook, TikTok og websites med god lyd og klipning.
              </p>
            </div>

            <div className="p-8 rounded-none bg-white border border-zinc-200 shadow-sm space-y-4">
              <div className="text-[#141416]">
                <i className="fa-solid fa-bolt text-2xl" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-serif text-[#141416]">Hurtig Levering</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Mulighed for ekspreslevering af udvalgte højdepunkter under eller umiddelbart efter løbet til pressebrug.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Customer testimonial for cycling events */}
      <TestimonialsSection
        category="cykelloeb"
        title="Anbefaling fra sport & cykelløb"
        subtitle="Hør hvad arrangørerne af Ironhorse Derby siger om samarbejdet med Lunde Media"
        theme="light"
      />

      {/* CTA Section (Cinematic Dark) */}
      <section className="py-20 bg-[#0C0C0D] border-t border-white/10 text-center">
        <div className="max-w-7xl mx-auto px-6 md:px-8 space-y-6">
          <h2 className="text-3xl sm:text-4xl font-serif text-[#F4EFEA] max-w-3xl mx-auto">
            Skal jeres cykelløb eller team dokumenteres?
          </h2>
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
            Kontakt Michael for at høre om tilgængelighed og muligheder for dækning af jeres næste løb.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt?ydelse=cykelloeb"
              className="cta-btn inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-stone-100 text-[#0C0C0D] font-semibold text-sm tracking-wide transition-all shadow-lg"
            >
              Kontakt Michael
            </Link>
            <a
              href="tel:26368306"
              className="cta-btn inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent hover:bg-transparent text-white font-medium text-sm tracking-wide transition-all border border-white"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span className="whitespace-nowrap">26 36 83 06</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
